import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { createAdminUser } from '../../api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface CreateAdminDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateAdminDialog({ open, onOpenChange }: CreateAdminDialogProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')

  const mutation = useMutation({
    mutationFn: () => createAdminUser({ display_name: displayName.trim(), email: email.trim(), role_slug: 'super-admin' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      toast.success(t('admin.toasts.userCreated'))
      onOpenChange(false)
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })

  function handleClose(open: boolean) {
    if (!open) {
      setDisplayName('')
      setEmail('')
    }
    onOpenChange(open)
  }

  const isValid = displayName.trim().length > 0 && email.trim().includes('@')

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (isValid) mutation.mutate()
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('admin.users.dialogs.create.title')}</DialogTitle>
            <DialogDescription>{t('admin.users.dialogs.create.description')}</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4 py-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="admin-display-name">
                {t('admin.users.dialogs.create.displayName')}
              </Label>
              <Input
                id="admin-display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t('admin.users.dialogs.create.displayNamePlaceholder')}
                autoComplete="name"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="admin-email">{t('admin.users.dialogs.create.email')}</Label>
              <Input
                id="admin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('admin.users.dialogs.create.emailPlaceholder')}
                autoComplete="email"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleClose(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={mutation.isPending || !isValid}>
              {mutation.isPending
                ? t('admin.users.dialogs.create.saving')
                : t('admin.users.dialogs.create.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
